import React, { useCallback, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, ClipboardList, LayoutGrid, Pencil } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/primitives/Dialog';
import { CourseWorkspace } from '../studio/components/course/CourseWorkspace';
import { CourseProfileEditor } from '../studio/components/coursePersona/CourseProfileEditor';
import { getSubjectById } from '../studio/constants/subjectCatalog';
import { LEARNING_STYLE_OPTIONS, resolveCourseDisplayName } from '../studio/constants/courseProfileUi';
import {
  deleteCourseProfile,
  getCourseProfileById,
  saveCourseProfile,
} from '../studio/services/courseProfileStorage';
import { listRubricsForCourse } from '../studio/services/rubricStorage';
import type { CourseProfile } from '../studio/types/courseProfile';

type CourseTab = 'materiales' | 'planificacion' | 'evaluacion';

function tagLabel(profile: CourseProfile): string[] {
  const tags: string[] = [];
  for (const interest of profile.interests.slice(0, 3)) {
    tags.push(interest);
  }
  for (const style of profile.learningStyle.slice(0, 2)) {
    const label = LEARNING_STYLE_OPTIONS.find((option) => option.id === style)?.label ?? style;
    if (!tags.includes(label)) tags.push(label);
  }
  return tags;
}

export function CourseDashboardPage() {
  const navigate = useNavigate();
  const { courseId = '' } = useParams<{ courseId: string }>();
  const [courseRefreshKey, setCourseRefreshKey] = useState(0);
  const course = useMemo(
    () => getCourseProfileById(courseId),
    [courseId, courseRefreshKey],
  );
  const [tab, setTab] = useState<CourseTab>('materiales');
  const [rubricRefreshKey, setRubricRefreshKey] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [courseDraft, setCourseDraft] = useState<CourseProfile | null>(null);
  const [savingCourse, setSavingCourse] = useState(false);
  const [courseStatus, setCourseStatus] = useState<string | null>(null);
  const rubrics = useMemo(
    () => listRubricsForCourse(courseId),
    [courseId, rubricRefreshKey],
  );
  const refreshRubrics = useCallback(() => {
    setRubricRefreshKey((key) => key + 1);
  }, []);

  const openCourseEditor = () => {
    if (!course) return;
    setCourseDraft({ ...course });
    setCourseStatus(null);
    setEditorOpen(true);
  };

  const handleSaveCourse = () => {
    if (!courseDraft) return;
    if (!courseDraft.level) {
      setCourseStatus('Elige el nivel curricular.');
      return;
    }

    setSavingCourse(true);
    try {
      saveCourseProfile({
        ...courseDraft,
        displayName: resolveCourseDisplayName(courseDraft),
        instrumentalProfile: courseDraft.instrumentalProfile?.instrumentsAvailable?.length
          ? courseDraft.instrumentalProfile
          : undefined,
      });
      setEditorOpen(false);
      setCourseDraft(null);
      setCourseRefreshKey((key) => key + 1);
    } finally {
      setSavingCourse(false);
    }
  };

  const handleDeleteCourse = () => {
    if (!course) return;
    const label = resolveCourseDisplayName(course);
    if (!window.confirm(`¿Eliminar el curso "${label}"?`)) return;
    deleteCourseProfile(course.id);
    navigate('/studio');
  };

  if (!course) {
    return (
      <div className="studio-page w-full min-w-0">
        <p className="text-[var(--sala-ink-muted)]">Curso no encontrado.</p>
        <Link to="/studio" className="sala-btn-outline mt-4 inline-flex">
          Volver al Studio
        </Link>
      </div>
    );
  }

  const subject = getSubjectById(course.subjectId);
  const tags = tagLabel(course);

  return (
    <div className="studio-page relative w-full min-w-0">
      <div className="relative z-[1] w-full min-w-0" data-subject-id={course.subjectId}>
        <Link to="/studio" className="studio-course-breadcrumb">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Todos los cursos
        </Link>

        <header className="studio-course-hero">
          <div className="studio-course-hero__lead min-w-0 flex-1">
            <p className="studio-course-hero__kicker">
              {subject?.name ?? course.subjectTag}
              {course.schoolName?.trim() ? ` · ${course.schoolName.trim()}` : ''}
            </p>
            <div className="studio-course-hero__title-row">
              <h1 className="studio-course-hero__title">{resolveCourseDisplayName(course)}</h1>
              <button
                type="button"
                className="studio-course-hero__pencil"
                onClick={openCourseEditor}
                aria-label="Editar perfil del curso"
                title="Editar perfil"
              >
                <Pencil className="h-4 w-4" strokeWidth={2} aria-hidden />
              </button>
            </div>
            {tags.length > 0 ? (
              <div className="studio-course-hero__tags">
                {tags.map((tag) => (
                  <span key={tag} className="studio-soft-chip">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </header>

        <nav className="studio-course-section-bar" aria-label="Secciones del curso">
          <button
            type="button"
            className={`studio-course-section-bar__btn${tab === 'materiales' ? ' studio-course-section-bar__btn--active' : ''}`}
            onClick={() => setTab('materiales')}
            aria-current={tab === 'materiales' ? 'page' : undefined}
          >
            <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
            Materiales
          </button>
          <button
            type="button"
            className={`studio-course-section-bar__btn${tab === 'planificacion' ? ' studio-course-section-bar__btn--active' : ''}`}
            onClick={() => setTab('planificacion')}
            aria-current={tab === 'planificacion' ? 'page' : undefined}
          >
            <CalendarDays className="h-3.5 w-3.5" aria-hidden />
            Planificación
          </button>
          <button
            type="button"
            className={`studio-course-section-bar__btn${tab === 'evaluacion' ? ' studio-course-section-bar__btn--active' : ''}`}
            onClick={() => setTab('evaluacion')}
            aria-current={tab === 'evaluacion' ? 'page' : undefined}
          >
            <ClipboardList className="h-3.5 w-3.5" aria-hidden />
            Rúbricas
          </button>
        </nav>

        <div className="studio-course-content">
          <CourseWorkspace
            course={course}
            section={tab}
            rubrics={rubrics}
            onRubricsChanged={refreshRubrics}
          />
        </div>
      </div>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar perfil del curso</DialogTitle>
            <DialogDescription>
              Actualiza el contexto del grupo que usa la IA en materiales y evaluación.
            </DialogDescription>
          </DialogHeader>
          {courseStatus ? <p className="text-sm text-amber-700">{courseStatus}</p> : null}
          {courseDraft ? (
            <CourseProfileEditor
              profile={courseDraft}
              onChange={setCourseDraft}
              onSave={handleSaveCourse}
              onDelete={handleDeleteCourse}
              saving={savingCourse}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Redirige rutas legacy del Escritorio al hub course-centric. */
export function EscritorioRedirectPage() {
  return <Navigate to="/studio" replace />;
}
